import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type {
  InvoiceEntity,
  UpdateInvoiceReqBody,
} from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'

import { dealQueryKey } from './useDealQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

export interface UpdateInvoiceVariables {
  dealId: string
  invoiceId: string
  /**
   * Optimistic patch applied to the invoices query cache synchronously. Mock /
   * backend recomputes derived fields server-side; the deals list cache is
   * refreshed via onSettled invalidate (no longer mirrored client-side because
   * Deal no longer embeds invoices[]).
   */
  patch: Partial<InvoiceEntity>
  body: UpdateInvoiceReqBody
}

interface OptimisticContext {
  invoicesSnapshot: { key: readonly unknown[]; data: InvoiceEntity[] | undefined }
}

function applyPatchToInvoices(
  invoices: InvoiceEntity[],
  invoiceId: string,
  patch: Partial<InvoiceEntity>,
): InvoiceEntity[] {
  return invoices.map((invoice) =>
    invoice.id === invoiceId ? { ...invoice, ...patch } : invoice,
  )
}

export function useUpdateInvoiceMutation(): UseMutationResult<
  InvoiceEntity,
  unknown,
  UpdateInvoiceVariables,
  OptimisticContext
> {
  const queryClient = useQueryClient()
  const handleError = useApiErrorHandler()

  return useMutation<InvoiceEntity, unknown, UpdateInvoiceVariables, OptimisticContext>(
    ({ dealId, invoiceId, body }) => dealsAPI.updateInvoice(dealId, invoiceId, body),
    {
      onMutate: async ({ dealId, invoiceId, patch }): Promise<OptimisticContext> => {
        await queryClient.cancelQueries({ queryKey: invoicesQueryKey(dealId) })

        const invoicesKey = invoicesQueryKey(dealId)
        const invoicesData = queryClient.getQueryData<InvoiceEntity[]>(invoicesKey)
        if (invoicesData) {
          queryClient.setQueryData<InvoiceEntity[]>(
            invoicesKey,
            applyPatchToInvoices(invoicesData, invoiceId, patch),
          )
        }

        return { invoicesSnapshot: { key: invoicesKey, data: invoicesData } }
      },
      onError: (error, _vars, context) => {
        if (context) {
          queryClient.setQueryData(context.invoicesSnapshot.key, context.invoicesSnapshot.data)
        }
        handleError(error)
      },
      onSettled: (_data, _err, { dealId }) => {
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(dealId) })
      },
    },
  )
}
